import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useUserDocument } from './UserDocumentContext';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { userDocument, updateUserDocument } = useUserDocument();
  
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage for saved theme preference (fallback)
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      // console.log('Initial theme from localStorage:', savedTheme);
      return savedTheme;
    }
    // Default to dark theme
    // console.log('Using default theme: dark');
    return 'dark';
  });

  // Load theme from Firestore when user document is available
  useEffect(() => {
    if (userDocument?.globalSettings?.display?.theme) {
      const firestoreTheme = userDocument.globalSettings.display.theme as Theme;
      if (firestoreTheme === 'light' || firestoreTheme === 'dark') {
        // Always update theme from Firestore when user document loads
        setThemeState(firestoreTheme);
        localStorage.setItem('theme', firestoreTheme);
        // console.log('Theme loaded from Firestore:', firestoreTheme);
      }
    }
  }, [userDocument]);

  const setTheme = async (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    
    // Save to Firestore if user is authenticated
    if (user && updateUserDocument) {
      try {
        await updateUserDocument({
          globalSettings: {
            ...userDocument?.globalSettings,
            display: {
              ...userDocument?.globalSettings?.display,
              theme: newTheme
            }
          }
        });
        // console.log('Theme saved to Firestore:', newTheme);
      } catch (error) {
        console.error('Failed to save theme to Firestore:', error);
        // Continue with local storage as fallback
      }
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  // Apply theme to document body
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.remove('light');
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
    }
  }, [theme]);

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
